/**
 * State + thunks for the Creator Settings → Assignments tab.
 *
 * The source of truth lives on currentUser.privateData.assignments,
 * but we fetch via the server endpoint (/api/creator/assignments) so
 * the data is freshly read on every page visit — pending invites
 * could have been created by an Org between two visits.
 *
 * Spec: UPDATE.md §3.6.2, §4.5
 */

import { fetchCurrentUser } from '../../ducks/user.duck';
import { assignmentsAPI } from '../../api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_REQUEST = 'app/CreatorSettingsAssignmentsPage/FETCH_REQUEST';
export const FETCH_SUCCESS = 'app/CreatorSettingsAssignmentsPage/FETCH_SUCCESS';
export const FETCH_ERROR = 'app/CreatorSettingsAssignmentsPage/FETCH_ERROR';

export const RESPOND_REQUEST = 'app/CreatorSettingsAssignmentsPage/RESPOND_REQUEST';
export const RESPOND_SUCCESS = 'app/CreatorSettingsAssignmentsPage/RESPOND_SUCCESS';
export const RESPOND_ERROR = 'app/CreatorSettingsAssignmentsPage/RESPOND_ERROR';

// ================ Reducer ================ //

const initialState = {
  loading: false,
  loaded: false,
  loadError: null,
  assignments: [],
  // Live org branding (name + avatarUrl) keyed by organizationProfileId.
  // Resolved server-side so logo/name changes show up without needing to
  // rewrite each assignment record's snapshot.
  orgs: {},

  // Per-assignment in-flight state for accept/reject buttons
  respondingId: null,
  respondError: null,
};

const upsertAssignment = (list, record) => {
  if (!record?.id) return list;
  const idx = list.findIndex(a => a?.id === record.id);
  if (idx < 0) return [...list, record];
  const next = [...list];
  next[idx] = record;
  return next;
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_REQUEST:
      return { ...state, loading: true, loadError: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        assignments: payload?.assignments || [],
        orgs: payload?.orgs || {},
      };
    case FETCH_ERROR:
      return { ...state, loading: false, loadError: payload };

    case RESPOND_REQUEST:
      return { ...state, respondingId: payload, respondError: null };
    case RESPOND_SUCCESS:
      return {
        ...state,
        respondingId: null,
        assignments: upsertAssignment(state.assignments, payload),
      };
    case RESPOND_ERROR:
      return { ...state, respondingId: null, respondError: payload };

    default:
      return state;
  }
}

// ================ Thunks ================ //

const unwrap = res => {
  const body = res?.data ?? res ?? {};
  return body?.data ?? body;
};

export const fetchCreatorAssignments = () => async dispatch => {
  dispatch({ type: FETCH_REQUEST });
  try {
    const res = await assignmentsAPI.listForCreator();
    const { assignments, orgs } = unwrap(res);
    dispatch({
      type: FETCH_SUCCESS,
      payload: { assignments: assignments || [], orgs: orgs || {} },
    });
    return assignments;
  } catch (e) {
    dispatch({ type: FETCH_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const acceptCreatorAssignment = (
  assignmentId,
  grantedConsents
) => async dispatch => {
  dispatch({ type: RESPOND_REQUEST, payload: assignmentId });
  try {
    const res = await assignmentsAPI.accept(assignmentId, { grantedConsents });
    const { assignment } = unwrap(res);
    dispatch({ type: RESPOND_SUCCESS, payload: assignment });
    // Refresh currentUser so other pages reading privateData.assignments
    // stay in sync without a hard reload.
    dispatch(fetchCurrentUser()).catch(() => null);
    return assignment;
  } catch (e) {
    dispatch({ type: RESPOND_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const rejectCreatorAssignment = (
  assignmentId,
  reason
) => async dispatch => {
  dispatch({ type: RESPOND_REQUEST, payload: assignmentId });
  try {
    const res = await assignmentsAPI.reject(assignmentId, reason ? { reason } : {});
    const { assignment } = unwrap(res);
    dispatch({ type: RESPOND_SUCCESS, payload: assignment });
    dispatch(fetchCurrentUser()).catch(() => null);
    return assignment;
  } catch (e) {
    dispatch({ type: RESPOND_ERROR, payload: storableError(e) });
    throw e;
  }
};

// Creator ends their own active assignment (audience sovereignty control).
export const revokeCreatorAssignment = assignmentId => async dispatch => {
  dispatch({ type: RESPOND_REQUEST, payload: assignmentId });
  try {
    const res = await assignmentsAPI.revoke(assignmentId);
    const { assignment } = unwrap(res);
    dispatch({ type: RESPOND_SUCCESS, payload: assignment });
    dispatch(fetchCurrentUser()).catch(() => null);
    return assignment;
  } catch (e) {
    dispatch({ type: RESPOND_ERROR, payload: storableError(e) });
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = () => async dispatch => {
  await dispatch(fetchCurrentUser());
  await dispatch(fetchCreatorAssignments()).catch(() => null);
  return null;
};
